import * as Yup from 'yup';

/**
 * Schema: layerSchema
 * 
 * This schema defines the validation rules for a single layer object using Yup.
 * 
 * @property {string} location - The location of the layer (required).
 * @property {number} thickness - The thickness of the layer (required, minimum value of 1).
 * @property {string} function - The function of the layer (required, one of several predefined values).
 */
const layerSchema = Yup.object().shape({
  location: Yup.string().required('Location is required'),
  thickness: Yup.number()
    .required('Thickness is required')
    .moreThan(0, 'Thickness must be bigger than 0.'),
  function: Yup.mixed()
    .oneOf([
      'STRUCTURE',
      'SUBSTRATE',
      'THERMAL_AIR',
      'FINISH1',
      'FINISH2',
      'MEMBRANE',
    ])
    .required('Function is required'),
});

/**
 * Schema: layersSchema
 * 
 * This schema defines the validation rules for the layers object, which includes core, interior, and exterior arrays of layers.
 * 
 * @property {Array} core - An array of core layers (required, must have at least one item).
 * @property {Array} interior - An array of interior layers (required).
 * @property {Array} exterior - An array of exterior layers (required).
 */
const layersSchema = Yup.object().shape({
  core: Yup.array()
    .of(layerSchema)
    .min(1, 'Core must have at least one item')
    .required('Core is required'),
  interior: Yup.array().of(layerSchema).required('Interior is required'),
  exterior: Yup.array().of(layerSchema).required('Exterior is required'),
});

/**
 * Function: getTypeStructureSchema
 * 
 * This function returns a Yup schema for validating the structure of a type object.
 * The schema includes validation rules for name, description, side, category, and layers.
 * 
 * @returns {Yup.ObjectSchema} The Yup schema for validating a type structure.
 */
export const getTypeStructureSchema = () => {
  const typeStructureSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    description: Yup.string().optional(),
    category: Yup.string()
      .oneOf(['WALLS', 'FLOORS', 'ROOFS', 'DOORS', 'WINDOWS'])
      .required('Category is required'),
    layers: layersSchema.required('Layers are required'),
  });

  return typeStructureSchema;
};

/**
 * Function: getWallStructureSchema
 * 
 * This function returns a Yup schema for validating the wall type object.
 * The schema includes validation rules for name, description, side, category, and layers.
 * 
 * @returns {Yup.ObjectSchema} The Yup schema for validating a type structure.
 */
export const getWallStructureSchema = () => {
  const typeStructureSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    description: Yup.string().optional(),
    side: Yup.string()
      .oneOf(['INTERIOR', 'EXTERIOR'])
      .required('Side is required'),
    category: Yup.string()
      .oneOf(['WALLS', 'FLOORS', 'ROOFS', 'DOORS', 'WINDOWS'])
      .required('Category is required'),
    layers: layersSchema.required('Layers are required'),
  });

  return typeStructureSchema;
};